import React from 'react';
import { Alert, AlertTitle, Box, Link } from '@mui/material';
import { useTranslation } from 'react-i18next';

import { palette, spaces } from '../../assets/styles/themes/tokens';
import { emailAddresses } from '../../constants';
import { SubscriptionStatus } from '../../utils/useSubscriptionInfoData';

interface Props {
  subscriptionStatus: SubscriptionStatus;
  endDate: string;
  pauseStartDate?: string;
  pauseEndDate?: string;
  isBusinessUser: boolean;
  withdrawalSubmittedAt?: string;
}

const SubscriptionStatusAlert: React.FC<Props> = ({
  subscriptionStatus,
  endDate,
  pauseStartDate,
  pauseEndDate,
  isBusinessUser,
  withdrawalSubmittedAt,
}) => {
  const { t } = useTranslation();

  const alertStyle = {
    borderColor: palette.info.main,
    color: palette.info.dark,
    paddingX: spaces.md,
    paddingY: spaces.sm,
    marginTop: spaces.lg,
    fontFamily: 'Poppins',
    fontSize: '14px',
    fontStyle: 'normal',
    fontWeight: 300,
    lineHeight: '20px',
    letterSpacing: '0.25px',
  };

  const alertTitleStyle = {
    color: palette.info.dark,
    fontFamily: 'Borna',
    fontSize: '16px',
    fontStyle: 'normal',
    fontWeight: 500,
    lineHeight: '24px',
    letterSpacing: '0.15px',
  };

  switch (subscriptionStatus) {
    case SubscriptionStatus.WithdrawalRequested:
      return (
        <Box>
          <Alert severity='info' variant='outlined' sx={alertStyle}>
            <AlertTitle sx={alertTitleStyle}>
              {t('Your withdrawal is being processed')}
            </AlertTitle>
            {withdrawalSubmittedAt
              ? t(
                'We received your withdrawal on {{submissionDateTime}}.' +
                ' You will receive a confirmation by email.',
                { submissionDateTime: withdrawalSubmittedAt },
              )
              : t('We received your withdrawal. You will receive a confirmation by email.')}
          </Alert>
        </Box>
      );
    case SubscriptionStatus.PauseRequested:
      return (
        <Box>
          <Alert severity='info' variant='outlined' sx={alertStyle}>
            <AlertTitle sx={alertTitleStyle}>
              {`${t('The pause of your Premium access is being processed')}.`}
            </AlertTitle>
            {
              `${t('You will receive a confirmation email from us once the process is complete.' +
                ' If you have any questions, please contact our support team at ')}`
            }
            <Link href={`mailto:${emailAddresses.support}`} underline='none'>
              {emailAddresses.support}
            </Link>
          </Alert>
        </Box>
      );
    case SubscriptionStatus.Paused:
      return (
        <Box>
          <Alert
            severity='info'
            variant='outlined'
            sx={alertStyle}
          >
            <AlertTitle sx={alertTitleStyle}>
              {t('Your subscription will be paused from {{pauseStartDate}} bis zum {{pauseEndDate}}',
                { pauseStartDate, pauseEndDate }
              )}
            </AlertTitle>
            {
              `${t('You currently do not have Premium access because you have agreed to a pause.' +
                ' If you would like to end your pause early, please contact the ')}`
            }
            <Link href={`mailto:${emailAddresses.support}`} underline='none'>
              {emailAddresses.support}
            </Link>
          </Alert>
        </Box>
      );
    case SubscriptionStatus.Cancelled:
      return (
        <Box>
          <Alert
            severity='info'
            variant='outlined'
            sx={alertStyle}
          >
            {isBusinessUser ? undefined :
              <AlertTitle sx={alertTitleStyle}>
                {`${t('Your subscription will be available until')} ${endDate}.`}
              </AlertTitle>}
            {isBusinessUser
              ? `${t('Your subscription will be available until')} ${endDate}.`
              : t('You can enjoy your premium benefits until the end of your subscription')}
          </Alert>
        </Box>
      );
    case SubscriptionStatus.Blocked:
      return (
        <Box>
          <Alert
            severity='warning'
            variant='outlined'
            sx={{
              ...alertStyle,
              borderColor: palette.warning.main,
              color: palette.warning.dark,
            }}
          >
            <AlertTitle sx={{ ...alertTitleStyle, color: palette.warning.dark }}>
              {t('Your subscription has been suspended due to the lack of payment')}
            </AlertTitle>
            {t('Please pay the bill to renew your subscription. ' +
              'If you have any questions, contact support via chat or ')}
            <Link href={`mailto:${emailAddresses.support}`} underline='none'>
              {emailAddresses.support}
            </Link>
          </Alert>
        </Box>
      );
    default:
      return null;
  }
};

export default SubscriptionStatusAlert;
